<template>
  <div class="area-panel d-flex flex-column h-100">
    <!-- HEADER (not scrollable) -->
    <div class="area-header">
      <!-- breadcrumbs row -->
      <div
        class="breadcrumbs font-weight-medium px-4 py-2 d-flex align-center justify-space-between"
      >
        <!-- left side: breadcrumbs -->
        <div class="d-flex align-center">
          <v-btn
            variant="plain"
            density="compact"
            class="px-0 opacity-100 font-weight-medium"
            style="text-transform: none; font-size: 1rem"
            :color="
              selectedAreaDataLevelId != null ? 'primary' : 'grey-darken-4'
            "
            :disabled="selectedAreaDataLevelId == null"
            @click="
              () => {
                this.areaTypeChangeConfirmed();
              }
            "
            >Area types</v-btn
          >
          <span
            v-if="selectedAreaDataLevelId != null"
            class="crumb-trail"
            style="font-size: 1rem"
          >
            <v-icon icon="mdi-chevron-right" />
            {{ selectedDataLevelName }}
          </span>
        </div>

        <!-- right side: timepoint dropdown -->
        <v-select
          v-if="selectedAreaDataLevelId != null"
          variant="plain"
          density="compact"
          hide-details
          class="w-auto min-w-0 flex-0 ma-0 pa-0 text-body-1 timepoint-select"
          style="width: auto; max-width: 80px"
          :items="selectedDataLevelTimepoints"
          item-value="label"
          :model-value="selectedDataLevelTimepoint"
          @update:modelValue="
            (val) => {
              pendingTimepoint = val;
              this.timepointChangeConfirmed();
            }
          "
        ></v-select>
      </div>
      <v-divider></v-divider>

      <!-- search-->
      <div class="search pa-4" v-if="showAreas">
        <v-text-field
          variant="plain"
          density="compact"
          autocomplete="off"
          hide-details
          clearable
          class="w-100"
          :placeholder="'Search ' + selectedDataLevelName"
          prepend-inner-icon="mdi-magnify"
          v-model="searchText"
          ><template #append-inner>
            <v-chip
              v-if="showSearchLimitWarning"
              variant="tonal"
              size="small"
              class="ml-2"
              color="blue-darken-4"
            >
              First {{ MAX_OPEN_PARENTS }} groups highlighted
            </v-chip>
          </template></v-text-field
        >
      </div>
      <v-divider v-if="showAreas" class="border-opacity-25"></v-divider>
    </div>

    <!-- BODY (scrollable) -->
    <div class="area-body overflow-y-auto">
      <!-- Area Data Level List (LA, MSOA etc) -->
      <template v-if="!showAreas">
        <template v-if="areaDataLevelsLoading">
          <v-skeleton-loader
            v-for="n in 10"
            :key="n"
            type="list-item"
          ></v-skeleton-loader>
        </template>
        <v-list v-else role="group" aria-label="Choose Area Type" class="py-0">
          <template v-for="item in sortedAreaDataLevels" :key="item.area_level">
            <v-hover v-slot="{ isHovering, props }">
              <v-list-item
                v-bind="props"
                tag="button"
                type="button"
                class="text-left justify-space-between w-100"
                :class="isHovering ? 'bg-grey-lighten-4' : undefined"
                append-icon="mdi-chevron-right"
                @click="setSelectedAreaDataLevelId(item.area_level)"
              >
                <v-list-item-title>
                  {{ item.area_level_name }}
                </v-list-item-title>
              </v-list-item>
            </v-hover>
          </template>
        </v-list>
      </template>

      <!-- Areas List (for selected data level) -->
      <template v-if="showAreas">
        <template v-if="fetchingAreas">
          <v-skeleton-loader
            v-for="n in 10"
            :key="n"
            type="list-item"
          ></v-skeleton-loader>
        </template>
        <div v-else>
          <v-list density="compact" lines="one" class="py-0">
            <template
              v-for="(group, index) in groupedAreas"
              :key="group.letter"
            >
              <!-- Alphabetical subheader-->
              <v-list-subheader
                class="bg-grey-lighten-4 border-b-thin font-weight-medium"
                :class="{ 'border-t-thin': index > 0 }"
              >
                {{ group.letter }}
              </v-list-subheader>
              <!-- Grouped by parent -->
              <template v-if="groupAreasByParent">
                <v-expansion-panels
                  :accordion="true"
                  class="mb-2"
                  :flat="true"
                  v-model="openParentIds"
                >
                  <v-expansion-panel
                    v-for="parent in group.areas"
                    :key="parent.parent_id"
                    :value="parent.parent_id"
                  >
                    <v-expansion-panel-title
                      class="pa-0 pr-2"
                      density="compact"
                    >
                      <div
                        class="d-flex align-center w-100 pa-0 ma-0 pl-2 font-weight-medium"
                      >
                        <v-checkbox
                          :aria-label="'select' + parent.parent_name"
                          class="ma-0 pa-0"
                          density="compact"
                          color="primary"
                          hide-details
                          :model-value="isParentChecked(parent)"
                          :indeterminate="isParentIndeterminate(parent)"
                          @update:modelValue="
                            (checked) => toggleParent(parent, checked)
                          "
                          @click.stop
                        />
                        <span
                          class="ml-2 d-flex align-center"
                          style="font-size: 1rem"
                          v-html="highlight(parent.parent_name)"
                        ></span>
                      </div>
                    </v-expansion-panel-title>

                    <v-expansion-panel-text density="compact" class="pa-0">
                      <v-list density="compact" lines="one" class="py-0">
                        <v-list-item
                          v-for="area in parent.areas"
                          :key="area.id"
                        >
                          <v-list-item-title>
                            <div class="d-flex align-center w-100 pa-0 ma-0">
                              <v-checkbox
                                :aria-label="'select' + area.area_name"
                                class="ma-0 pa-0"
                                density="compact"
                                color="primary"
                                hide-details
                                :model-value="selectedAreaIds.includes(area.id)"
                                @update:modelValue="
                                  (checked) => {
                                    checked
                                      ? addSelectedAreaIds(area.id)
                                      : removeSelectedAreaIds(area.id);
                                  }
                                "
                              />
                              <!-- area name with search highlighting -->
                              <span
                                class="ml-2 d-flex align-center"
                                style="font-size: 1rem"
                                v-html="highlight(area.area_name)"
                              ></span>
                            </div>
                          </v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </template>
              <!-- Ungrouped entries -->
              <template v-else v-for="area in group.areas" :key="area.id">
                <v-list-item class="pa-0">
                  <v-list-item-title>
                    <div class="d-flex align-center w-100 pa-0 ma-0 pl-2">
                      <v-checkbox
                        :aria-label="'select' + area.area_name"
                        class="ma-0 pa-0"
                        density="compact"
                        color="primary"
                        hide-details
                        :model-value="selectedAreaIds.includes(area.id)"
                        @update:modelValue="
                          (checked) => {
                            checked
                              ? addSelectedAreaIds(area.id)
                              : removeSelectedAreaIds(area.id);
                          }
                        "
                      />
                      <!-- area name with search highlighting -->
                      <span
                        class="ml-2 d-flex align-center"
                        v-html="highlight(area.area_name)"
                      ></span>
                    </div>
                  </v-list-item-title>
                </v-list-item>
              </template>
            </template>
          </v-list>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { systemMessages } from "@/mixins/SystemMessages";
import { Debounce } from "@/mixins/Debounce";

import { createNamespacedHelpers } from "vuex";
const { mapState, mapMutations, mapGetters } =
  createNamespacedHelpers("customAreaManager");

export default {
  name: "AreaListStandard",
  components: {},
  mixins: [systemMessages, Debounce],
  props: {},
  computed: {
    //states from VUEX to read values e.g. this.standardAreas
    ...mapState([
      "areaDataLevelTimepoints",
      "areaDataLevels",
      "selectedAreaDataLevelId",
      "selectedDataLevelTimepoint",
      "selectedAreaIds",
      "standardAreas",
      "fetchingAreas",
      "standardAreaParents",
    ]),
    //getters from VUEX to read derived values
    ...mapGetters([
      "areaDataLevelsLoading",
      "latestTimepointForAreaLevel",
      "selectedDataLevelName",
      "selectedDataLevelTimepoints",
      "areaDataForLevelTimepoint",
      "parentsForAreaLevelTimepoint",
    ]),

    /** Order area data levels by a country-specific priority, then sensible fallbacks (area_level/name).
     * This solution is the simplest implementation that orders data levels in a user-friendly way for this
     * component only.  If a site-wide solution is needed, then the AreaDataLevelController.listAreaDataLevels()
     * function and API endpoints should be refactored to handle ordering and return results.
     *
     * Country detection:
     * - Uses entity_code prefix (E/S/W) on items returned by BE
     * - Synthetic LA entry has entity_code 'Many' so is ignored for detection
     *
     * Priority lists are arrays of area_level codes in display order.
     * Quick reference: 1=LSOA, 2=MSOA, 5=Ward, 6=LA, 7=Combined Authority, 10=County, 12=Region, 14=WPC
     */
    sortedAreaDataLevels() {
      const levels = Array.isArray(this.areaDataLevels)
        ? [...this.areaDataLevels]
        : [];
      if (!levels.length) return levels;

      // Determine country from any non-synthetic item's entity_code prefix
      const prefixEntry = levels.find(
        (l) =>
          typeof l?.entity_code === "string" && /^[ESW]/i.test(l.entity_code),
      );
      const country = prefixEntry
        ? prefixEntry.entity_code[0].toUpperCase()
        : null;

      // Country-specific display orders (area_level arrays). Edit here as needed.
      const ordersByCountry = {
        E: [1, 2, 5, 6, 14, 7, 10, 12], // England
        W: [1, 2, 5, 6, 14], // Wales
        S: [1, 2, 5, 6, 14], // Scotland
      };

      const orderList =
        country && ordersByCountry[country] ? ordersByCountry[country] : null;

      // Build quick lookup: area_level -> rank (lower rank = earlier)
      const rankByAreaLevel = orderList
        ? new Map(orderList.map((levelCode, index) => [levelCode, index]))
        : null;

      levels.sort((leftItem, rightItem) => {
        const leftLevel = leftItem?.area_level ?? Number.POSITIVE_INFINITY;
        const rightLevel = rightItem?.area_level ?? Number.POSITIVE_INFINITY;

        if (rankByAreaLevel) {
          const leftRank = rankByAreaLevel.has(leftLevel)
            ? rankByAreaLevel.get(leftLevel)
            : Number.POSITIVE_INFINITY;
          const rightRank = rankByAreaLevel.has(rightLevel)
            ? rankByAreaLevel.get(rightLevel)
            : Number.POSITIVE_INFINITY;
          if (leftRank !== rightRank) return leftRank - rightRank;
        }

        // Fallback: numeric area_level ascending
        if (leftLevel !== rightLevel) return leftLevel - rightLevel;

        // Final tie-breaker: alphabetic by name
        const leftName = (leftItem?.area_level_name || "").toString();
        const rightName = (rightItem?.area_level_name || "").toString();
        return leftName.localeCompare(rightName);
      });

      return levels;
    },

    /** boolean - does area data level require grouoping by parent area */
    groupAreasByParent() {
      return this.areaLevelGroupByParent.includes(this.selectedAreaDataLevelId);
    },
    /** normalise search string (case/accent insensitive) */
    searchNorm() {
      const s = (this.debouncedSearch || "").trim();

      //ignore searches smaller than 3 characters (performance)
      if (s.length < 3) return "";

      return (this.debouncedSearch || "")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "") // strip accents
        .trim()
        .toUpperCase();
    },
    /** filter areas using search string */
    filteredAreas() {
      const search = this.searchNorm;
      if (this.groupAreasByParent) {
        // grouped: [{ parent_id, parent_name, areas: [...] }]
        const parents = this.areaDataForLevelTimepoint || [];
        if (!search) return parents;

        return parents
          .map((p) => {
            const children = (p.areas || []).filter((a) =>
              a._aname_normalised.includes(search),
            );
            const parentMatch = p._pname_normalised.includes(search);
            if (parentMatch) {
              //if parent matches, show all children
              return { ...p, areas: p.areas || [] };
            }
            //otherwise show parent with matching children
            return children.length ? { ...p, areas: children } : null;
          })
          .filter(Boolean);
      } else {
        // flat: [{ id, area_name, ... }]
        const areas = this.areaDataForLevelTimepoint || [];
        if (!search) return areas;
        return areas.filter((a) => a._aname_normalised.includes(search));
      }
    },
    /** return areas as alphabetical groups or parent groups for display */
    groupedAreas() {
      const keyOf = (label) => {
        const s = (label || "")
          .normalize("NFKD")
          .replace(/[\u0300-\u036f]/g, "")
          .trim()
          .toUpperCase();
        return /^[A-Z]/.test(s) ? s[0] : "#";
      };

      if (this.groupAreasByParent) {
        // bucket parents by first letter of parent_name
        const groups = [];
        let current = null;
        for (const p of this.filteredAreas) {
          const letter = keyOf(p.parent_name);
          if (!current || current.letter !== letter) {
            current = { letter, areas: [] };
            groups.push(current);
          }
          current.areas.push(p); // p.areas are children
        }
        return groups;
      } else {
        // bucket flat areas by first letter of area_name
        const groups = [];
        let current = null;
        for (const a of this.filteredAreas) {
          const letter = keyOf(a.area_name);
          if (!current || current.letter !== letter) {
            current = { letter, areas: [] };
            groups.push(current);
          }
          current.areas.push(a);
        }
        return groups;
      }
    },
    /** returns selected area ids array as set for faster searches */
    selectedAreaIdsSet() {
      return new Set(this.selectedAreaIds);
    },
    /** compile single search highlight regex for  reuse to improve performance*/
    highlightRegex() {
      const q = this.debouncedSearch?.trim();
      if (!q) return null;
      const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      return new RegExp(escaped, "ig");
    },
    /** how many parent groups matched the search */
    filteredParentCount() {
      return this.groupAreasByParent ? this.filteredAreas?.length || 0 : 0;
    },
    /** notify users if we've limited number of expanded search groups (performance) */
    showSearchLimitWarning() {
      return (
        this.groupAreasByParent &&
        this.searchNorm &&
        this.filteredParentCount >= this.MAX_OPEN_PARENTS
      );
    },
  },
  data() {
    return {
      showAreas: false, //show areas list for selected data level
      areaLevelGroupByParent: [1, 2, 5], //area levels that need to be grouped by level 6 (LA) parent
      pendingTimepoint: null, //selected timepoint to change to
      searchText: "", //search text to filter areas
      debouncedSearch: "", //debounced version of search text
      openParentIds: [], //array of parent ids that are open in grouped view
      MAX_OPEN_PARENTS: 20, //maximum number of parents to auto open when searching (performance)
    };
  },
  mounted() {
    //fetch available timepoints & area levels at initial load
    this.fetchAvailableDataLevelTimepoints();
    this.fetchAreaDataLevels();
  },
  created() {
    /**
     * a way to specify a watcher on two values using a closure.
     * watches selected data level and selected timepoint.  Only fetches
     * when both are set. Reacts to either changing.  Handles initial load
     * from data level picker and changing years
     */
    this.$watch(
      () => [this.selectedAreaDataLevelId, this.selectedDataLevelTimepoint],
      async ([levelId, tp]) => {
        if (!levelId) {
          //wait for level to be selected
          this.showAreas = false;
          return;
        }
        if (!tp) return; //wait for timepoint to be loaded
        await this.fetchAreasForLevel(); // single place that triggers fetch
      },
    );
    //set up debounced search
    this.debounceTextWatcher = this.debounce((val) => {
      this.debouncedSearch = val;
    }, 300);
  },
  watch: {
    selectedAreaDataLevelId: {
      handler(val) {
        if (val) {
          //set latest timepoint because we've changed data level
          const latestTP = this.latestTimepointForAreaLevel;
          this.setSelectedDataLevelTimepoint(latestTP);
        } else {
          //show area level list if set to null
          this.showAreas = false;
        }
      },
    },
    searchText: {
      handler(val) {
        this.debounceTextWatcher(val);
      },
    },
    /** auto open grouped parents when searching */
    filteredAreas: {
      immediate: true,
      handler(list) {
        if (!this.groupAreasByParent) return;

        if (this.searchNorm) {
          // filteredAreas is an array of parent objects (already filtered)
          const ids = (list || [])
            .map((p) => p.parent_id)
            .filter((id) => id != null)
            .slice(0, this.MAX_OPEN_PARENTS);
          this.openParentIds = ids;
        } else {
          this.openParentIds = [];
        }
      },
    },
  },
  methods: {
    //mutations from VUEX to set state values e.g. this.setStandardAreas(value)
    ...mapMutations([
      "setAreaDataLevelTimepoints",
      "setAreaDataLevels",
      "setSelectedAreaDataLevelId",
      "setSelectedDataLevelTimepoint",
      "addSelectedAreaIds",
      "removeSelectedAreaIds",
      "clearSelectedAreaIds",
      "setStandardAreas",
      "setFetchingAreaDataLevels",
      "setFetchingTimepoints",
      "setFetchingAreas",
      "setStandardAreaParents",
      "clearAllCustomAreaNameOverrides",
    ]),
    /**
     * Area level details (name, abbreviation, id etc)
     * e.g. LA, Counties,MSOA, LSOA
     **/
    async fetchAreaDataLevels() {
      this.setFetchingAreaDataLevels(true);
      try {
        const response = await this.$axios.get("/list-area-data-levels");
        this.setAreaDataLevels(response.data);
      } catch (e) {
        const eDetail = this.errorExtraction(e);
        this.errorMsg({
          message: eDetail.message,
          title: "Error! Failed to get all data levels",
        });
      } finally {
        this.setFetchingAreaDataLevels(false);
      }
    },
    /**
     * Standard area timepoints - fetches an array of available
     * timepoints in LI for all standard area levels  e.g. LA, Counties etc.
     */
    async fetchAvailableDataLevelTimepoints() {
      this.setFetchingTimepoints(true);
      try {
        const response = await this.$axios.get("/get-timepoints-standard-area");
        this.setAreaDataLevelTimepoints(response.data);
      } catch (e) {
        const eDetail = this.errorExtraction(e);
        this.errorMsg({
          message: eDetail.message,
          title: "Error! Failed to get data level timepoints",
        });
      } finally {
        this.setFetchingTimepoints(false);
      }
    },
    /**
     * fetch areas for selected data level
     */
    async fetchAreasForLevel() {
      this.setFetchingAreas(true); // loading status
      this.showAreas = true; //show the areas list
      try {
        //we need to fetch parent area (level 6) for LA, MSOA and Ward (equivilents)
        if (this.groupAreasByParent) {
          await this.fetchParentsForAreas();
        }

        //check whether we've already fetched and cached areas
        const cachedAreas = this.areaDataForLevelTimepoint;
        if (!cachedAreas) {
          //if areas need to be grouped by parent get grouped results from backend
          let response = null;
          if (this.groupAreasByParent) {
            response = await this.$axios.get(
              `/areas-grouped-by-parent/${this.selectedAreaDataLevelId}/${this.selectedDataLevelTimepoint}`,
            );
          } else {
            response = await this.$axios.get(
              `/list-areas-by-level-int/${this.selectedAreaDataLevelId}/${this.selectedDataLevelTimepoint}`,
            );
          }
          this.setStandardAreas({
            dataLevelId: this.selectedAreaDataLevelId,
            timepoint: this.selectedDataLevelTimepoint,
            value: response.data,
          });
        }
      } catch (e) {
        const eDetail = this.errorExtraction(e);
        this.errorMsg({
          message: eDetail.message,
          title: "Error! Failed to get areas",
        });
      } finally {
        this.setFetchingAreas(false);
      }
    },
    /**
     * fetch parent level 6 (e.g. LA or equivalent) areas for timepoint
     * used to group LSOA, MSOA, Wards
     */
    async fetchParentsForAreas() {
      try {
        const cachedParents = this.parentsForAreaLevelTimepoint; //any parents already fetched for data level/timepoint
        if (!cachedParents) {
          const response = await this.$axios.get(
            `/parent-la-standard-area/${this.selectedDataLevelTimepoint}`,
          );
          this.setStandardAreaParents({
            timepoint: this.selectedDataLevelTimepoint,
            value: response.data,
          });
        }
      } catch (e) {
        const eDetail = this.errorExtraction(e);
        this.errorMsg({
          message: eDetail.message,
          title: "Error! Failed to get area parents",
        });
      } finally {
        //nothing yet
      }
    },
    /** return array of child area ids for a given group parent */
    childAreaIds(parent) {
      return (parent.areas || []).map((a) => a.id);
    },
    /** determine whether all children of group parent are checked */
    isParentChecked(parent) {
      const ids = this.childAreaIds(parent);
      if (!ids.length) return false;

      return ids.every((id) => this.selectedAreaIdsSet.has(id));
    },
    /** determine whether some but not all children of group parent are checked */
    isParentIndeterminate(parent) {
      const ids = this.childAreaIds(parent);
      if (!ids.length) return false;
      const selectedCount = ids.reduce(
        (acc, id) => acc + (this.selectedAreaIdsSet.has(id) ? 1 : 0),
        0,
      );
      return selectedCount > 0 && selectedCount < ids.length;
    },
    /** toggle group parent checkboxes between select all / clear all */
    toggleParent(parent, checked) {
      const ids = this.childAreaIds(parent);
      if (!ids.length) return;

      if (checked) {
        //add missing ids
        const toAdd = ids.filter((id) => !this.selectedAreaIdsSet.has(id));
        if (toAdd.length) this.addSelectedAreaIds(toAdd);
      } else {
        //remove all ids from parent
        const toRemove = ids.filter((id) => this.selectedAreaIdsSet.has(id));
        if (toRemove.length) this.removeSelectedAreaIds(toRemove);
      }
    },
    /** user confirmed timepoint change - reset selected ids */
    timepointChangeConfirmed() {
      this.setSelectedDataLevelTimepoint(this.pendingTimepoint);
      this.pendingTimepoint = null;
    },
    /** user confirmed area type change - reset ids */
    areaTypeChangeConfirmed() {
      this.setSelectedAreaDataLevelId(null);
    },
    /** helper function replaces special characters to sanitise text */
    escapeHtml(s) {
      return String(s).replace(
        /[&<>"']/g,
        (ch) =>
          ({
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#39;",
          })[ch],
      );
    },
    /** apply search filter highlighting to area names */
    highlight(text) {
      const t = text ?? "";
      const q = this.debouncedSearch?.trim();
      if (!q) return this.escapeHtml(t);
      if (q.length < 3) return this.escapeHtml(t); //ignore short searches

      // case-insensitive, escape regex metacharacters
      const re = this.highlightRegex;
      if (!re) return this.escapeHtml(t);

      //wrap full text in span to preserve whitespace
      let highlightText = `<span>${this.escapeHtml(t)}</span>`;
      // Replace matches with <mark> element
      return highlightText.replace(
        re,
        (m) => `<mark>${this.escapeHtml(m)}</mark>`,
      );
    },
    /**
     * helper function to handle error messages/types
     */
    errorExtraction(e) {
      const status = e?.response?.status;
      const serverData = e?.response?.data;
      const serverMsg =
        typeof serverData === "string"
          ? serverData
          : serverData?.message || serverData?.error || serverData?.detail;
      const canceled = e?.code === "ERR_CANCELED" || e?.message === "canceled";

      const msg = canceled ? "Request was canceled." : serverMsg || e?.message;

      return { status: status, message: msg };
    },
  },
};
</script>

<style scoped>
:deep(.v-expansion-panel-text__wrapper) {
  padding: 0 !important;
}

:deep(.v-expansion-panel--active:not(:first-child)) {
  margin-top: 0 !important;
}

.crumb-trail {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
}

.timepoint-select :deep(.v-field__input) {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

:deep(mark) {
  background-color: #f2eb70 !important;
}
</style>
